/**
 * The local-disk incremental backup engine.
 *
 * WHAT IT DRAINS FROM, AND WHY THERE IS NO SECOND CAPTURE PATH
 * ------------------------------------------------------------
 * The outbox IS the durable change log. `recordBusinessMutation` writes every
 * committed change into `syncOutbox` inside the SAME Dexie transaction as the
 * business data, so a change is never committed-but-unregistered. This engine
 * does not observe the UI or hook Dexie events — it drains that outbox to disk:
 *
 *   journal-NNNNNN.jsonl   append-only copy of each committed change, each with
 *                          a monotonic `seq`, written as soon as it is durable.
 *   checkpoint-NNNNNN.json a full snapshot in the portable-backup shape, taken
 *                          periodically so recovery replays a BOUNDED tail.
 *   manifest.json          cursor + metadata, rewritten atomically.
 *
 * Cloud sync drains the SAME outbox independently; neither layer requires the
 * other. Recovery = latest checkpoint + the journal entries after it.
 *
 * Everything here takes a `DiskBackupTarget`, so a test injects the in-memory
 * target and can simulate disk-full, permission denial and truncated writes.
 */
import type { DNRDatabase } from '@data/db/database';
import type { SyncOutboxEntry } from '@data/db/types';
import type { SyncChange } from '@domain/sync/changeModel';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { readActiveBusinessId, setActiveBusinessId } from '@data/services/businessSession';
import { toWireChange } from '@data/sync/outbox';
import { applyChanges } from '@data/sync/applyChanges';
import { createBackup, restoreBackup } from './backupService';
import type { DiskBackupTarget } from './diskBackupTarget';
import {
  LOCAL_BACKUP_FORMAT,
  LOCAL_BACKUP_FORMAT_VERSION,
  LOCAL_BACKUP_MANIFEST,
  checkpointFileName,
  journalFileName,
  validateManifest,
  parseJournal,
  journalSequencesOk,
  type LocalBackupManifest,
  type LocalBackupProblem,
} from './localBackupFormat';

/** Take a fresh checkpoint after this many journal entries. */
const CHECKPOINT_EVERY = 100;

export interface DiskBackupStatus {
  /** A manifest exists and the last write to it succeeded. */
  readonly enabled: boolean;
  readonly supported: boolean;
  readonly folderLabel: string;
  readonly checkpointSeq: number;
  readonly nextSeq: number;
  readonly pendingSinceCheckpoint: number;
  readonly lastBackupAt: string;
  readonly lastError?: string;
}

export interface DiskBackupRunResult {
  readonly ok: boolean;
  readonly status: DiskBackupStatus;
  readonly error?: string;
}

export interface DiskBackupReadResult {
  readonly ok: boolean;
  readonly manifest?: LocalBackupManifest;
  /** The parsed checkpoint (`BackupFile`), ready for `restoreBackup`. */
  readonly checkpoint?: unknown;
  readonly changes: readonly SyncChange[];
  readonly problem?: LocalBackupProblem;
  readonly message?: string;
}

export interface DiskBackupRestoreResult {
  readonly restored: Readonly<Record<string, number>>;
  readonly journalApplied: number;
  readonly journalSkipped: number;
  readonly activeBusinessId: string | null;
}

/** Stable order: by `createdAt`, then `changeId` to break timestamp ties. */
function byCursor(a: SyncOutboxEntry, b: SyncOutboxEntry): number {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.changeId < b.changeId ? -1 : 1;
}

/** Is `entry` strictly after the `(at, id)` journal cursor? */
function afterCursor(entry: SyncOutboxEntry, at: string, id: string): boolean {
  if (entry.createdAt !== at) return entry.createdAt > at;
  return entry.changeId > id;
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

async function writeManifest(
  target: DiskBackupTarget,
  manifest: LocalBackupManifest,
): Promise<void> {
  await target.writeFileAtomic(LOCAL_BACKUP_MANIFEST, JSON.stringify(manifest, null, 2));
}

/** Read the manifest, or null when absent or unparseable (treated as no backup). */
async function readManifestOrNull(target: DiskBackupTarget): Promise<LocalBackupManifest | null> {
  const text = await target.readFile(LOCAL_BACKUP_MANIFEST);
  if (text === null) return null;
  try {
    const validation = validateManifest(JSON.parse(text));
    return validation.ok && validation.manifest ? validation.manifest : null;
  } catch {
    return null;
  }
}

/**
 * Read the last valid journal line's sequence number and the cursor it represents.
 *
 * After a crash between journal append and manifest rewrite, the journal is the
 * durable record of what was committed. Returns null when the journal does not
 * exist, is empty, or is corrupt. Returns the last line's seq + its change
 * cursor (`changedAt`, `changeId`) otherwise, so the caller can adopt them and
 * avoid re-draining changes already persisted to disk.
 */
async function readJournalLastSeq(
  target: DiskBackupTarget,
  manifest: LocalBackupManifest,
): Promise<{ seq: number; at: string; id: string } | null> {
  const journalText = await target.readFile(journalFileName(manifest.checkpointSeq));
  if (journalText === null || journalText.trim() === '') return null;
  const parsed = parseJournal(journalText);
  if (parsed.problem || parsed.lines.length === 0) return null;
  const last = parsed.lines[parsed.lines.length - 1];
  if (!last) return null;
  const change = last.change as SyncChange | undefined;
  return {
    seq: last.seq,
    at: typeof change?.changedAt === 'string' ? change.changedAt : '',
    id: typeof change?.changeId === 'string' ? change.changeId : '',
  };
}

function statusFromManifest(
  target: DiskBackupTarget,
  manifest: LocalBackupManifest | null,
  lastError?: string,
): DiskBackupStatus {
  if (!manifest) {
    return {
      enabled: false,
      supported: target.supported,
      folderLabel: target.folderLabel(),
      checkpointSeq: 0,
      nextSeq: 0,
      pendingSinceCheckpoint: 0,
      lastBackupAt: '',
      lastError,
    };
  }
  return {
    enabled: true,
    supported: target.supported,
    folderLabel: target.folderLabel(),
    checkpointSeq: manifest.checkpointSeq,
    nextSeq: manifest.nextSeq,
    pendingSinceCheckpoint: manifest.pendingSinceCheckpoint,
    lastBackupAt: manifest.lastBackupAt,
    lastError,
  };
}

/**
 * Validate that an existing manifest is compatible with the current device and database.
 * Returns { compatible: true } if compatible, or { compatible: false, reason } if not.
 */
async function validateManifestCompatibility(
  manifest: LocalBackupManifest,
  deviceId: string,
  db: DNRDatabase,
  userId?: string,
): Promise<{ compatible: boolean; reason?: string }> {
  // Must be the same device that owns this backup
  if (manifest.deviceId !== deviceId) {
    return {
      compatible: false,
      reason:
        'This backup folder belongs to a different device. Choose a different folder or clear the folder first.',
    };
  }

  // Must be the same Supabase account that owns this backup
  if (userId && manifest.userId && manifest.userId !== userId) {
    return {
      compatible: false,
      reason:
        'This backup folder belongs to a different account. Choose a different folder or clear the folder first.',
    };
  }

  // Business IDs must match the current database
  const currentBusinessIds = new Set(
    (await db.businesses.toCollection().primaryKeys()).map(String),
  );
  const manifestBusinessIds = new Set(manifest.businessIds.map(String));

  if (
    manifestBusinessIds.size !== currentBusinessIds.size ||
    ![...manifestBusinessIds].every((id) => currentBusinessIds.has(id))
  ) {
    return {
      compatible: false,
      reason:
        'This backup folder contains data for different businesses. Choose a different folder or clear the folder first.',
    };
  }

  return { compatible: true };
}

/**
 * Pick a folder, write the first checkpoint, and initialise the manifest.
 * The folder is only reported as configured after that checkpoint write has
 * SUCCEEDED — an unsupported browser or a denied permission returns `ok: false`.
 *
 * If an existing valid backup exists in the folder, this validates compatibility:
 * - Same deviceId (same device owns the backup)
 * - Same businessIds (same business scope)
 * If compatible, resumes from existing state. If incompatible, refuses and
 * requires explicit user action to overwrite.
 */
export async function enableDiskBackup(
  target: DiskBackupTarget,
  db: DNRDatabase,
  now: string,
  userId?: string,
): Promise<DiskBackupRunResult> {
  if (!target.supported) {
    const error = 'Automatic disk backup is not supported on this device.';
    return { ok: false, status: statusFromManifest(target, null, error), error };
  }
  try {
    await target.pickFolder();

    // Check for existing valid backup in the folder
    const existingManifest = await readManifestOrNull(target);
    const deviceId = await currentDeviceId(db);

    if (existingManifest) {
      // Validate existing manifest compatibility
      const compat = await validateManifestCompatibility(existingManifest, deviceId, db, userId);
      if (!compat.compatible) {
        return {
          ok: false,
          status: statusFromManifest(target, existingManifest, compat.reason),
          error: compat.reason,
        };
      }
      // Compatible: resume from existing state
      const summary = await createBackup(db, { now });
      const manifest: LocalBackupManifest = {
        ...existingManifest,
        dataVersion: summary.dataVersion,
        businessIds: summary.businessIds,
        userId: userId ?? existingManifest.userId,
        lastBackupAt: now,
      };
      await writeManifest(target, manifest);
      return { ok: true, status: statusFromManifest(target, manifest) };
    }

    // No existing valid backup — create fresh
    const summary = await createBackup(db, { now });
    const manifest: LocalBackupManifest = {
      format: LOCAL_BACKUP_FORMAT,
      formatVersion: LOCAL_BACKUP_FORMAT_VERSION,
      app: 'DNR_Vyapar_2',
      dataVersion: summary.dataVersion,
      deviceId: await currentDeviceId(db),
      userId,
      businessIds: summary.businessIds,
      checkpointSeq: 0,
      checkpointFile: checkpointFileName(0),
      nextSeq: 1,
      lastBackupAt: now,
      pendingSinceCheckpoint: 0,
      lastJournaledAt: '',
      lastJournaledChangeId: '',
      generation: 1,
    };
    await writeManifest(target, manifest);
    // Write the initial checkpoint so the manifest's checkpointFile actually
    // exists on disk. Without this, the first readDiskBackup() fails with
    // MISSING_CHECKPOINT even though the manifest is valid.
    await target.writeFileAtomic(checkpointFileName(0), summary.json);
    return { ok: true, status: statusFromManifest(target, manifest) };
  } catch (e) {
    const error = errorMessage(e);
    let manifest: LocalBackupManifest | null = null;
    try {
      manifest = await readManifestOrNull(target);
    } catch {
      // The failure itself may have made the folder unreadable. Report the
      // ORIGINAL error; a second throw from inside the catch would escape
      // as an unhandled rejection and hide it.
    }
    return {
      ok: false,
      status: statusFromManifest(target, manifest, error),
      error,
    };
  }
}

/**
 * Drain newly committed outbox entries to the journal, then checkpoint when the
 * journal has grown enough. Safe to call on a timer: the cursor is read from the
 * manifest and only advanced by what was actually appended.
 */
export async function runDiskBackup(
  target: DiskBackupTarget,
  db: DNRDatabase,
  now: string,
  userId?: string,
): Promise<DiskBackupRunResult> {
  if (!target.supported) {
    const error = 'Automatic disk backup is not supported on this device.';
    return { ok: false, status: statusFromManifest(target, null, error), error };
  }
  let manifest: LocalBackupManifest | null;
  try {
    manifest = await readManifestOrNull(target);
  } catch (e) {
    const error = errorMessage(e);
    return { ok: false, status: statusFromManifest(target, null, error), error };
  }
  if (!manifest) {
    // Not yet configured (or manifest unreadable): do NOT auto-open the folder
    // picker. The File System Access API requires a user gesture to call
    // showDirectoryPicker, and an automatic prompt on login or refresh is
    // intrusive and often blocked. The user must explicitly click "Turn on"
    // in the Backup settings, which calls enableDiskBackup directly.
    return {
      ok: false,
      status: statusFromManifest(
        target,
        null,
        'Automatic backup is not configured. Choose a folder to enable it.',
      ),
      error: 'Automatic backup is not configured. Choose a folder to enable it.',
    };
  }

  // Validate account identity before writing to this folder. A different
  // account must not be able to drain its changes into another account's
  // backup folder.
  if (userId && manifest.userId && manifest.userId !== userId) {
    return {
      ok: false,
      status: statusFromManifest(
        target,
        manifest,
        'This backup folder belongs to a different account.',
      ),
      error:
        'This backup folder belongs to a different account. Choose a different folder or clear the folder first.',
    };
  }
  try {
    // CRASH RECOVERY: A crash between `appendFile` (journal) and `writeManifest`
    // (manifest) leaves the journal ahead of the manifest. On restart, using the
    // stale manifest.nextSeq would assign duplicate sequence numbers. Parse the
    // journal's last line and adopt its seq as the next expected, recovering the
    // cursor so changes already journaled are not re-drained.
    const journalSeq = await readJournalLastSeq(target, manifest);
    let cursorAt = manifest.lastJournaledAt ?? '';
    let cursorId = manifest.lastJournaledChangeId ?? '';
    let nextSeq = manifest.nextSeq;
    let pending = manifest.pendingSinceCheckpoint;

    if (journalSeq !== null && journalSeq.seq >= nextSeq) {
      // The journal was appended but the manifest was not updated. Recover the
      // cursor and nextSeq from the journal's last valid line. Re-derive
      // pendingSinceCheckpoint from the journal itself, since it is the durable
      // record of what was appended.
      nextSeq = journalSeq.seq + 1;
      cursorAt = journalSeq.at;
      cursorId = journalSeq.id;
      const journalText = await target.readFile(journalFileName(manifest.checkpointSeq));
      const parsed = parseJournal(journalText ?? '');
      if (!parsed.problem && journalSequencesOk(parsed.lines, manifest.checkpointSeq + 1)) {
        pending = parsed.lines.length;
      }
    }

    const all = await db.syncOutbox.toArray();
    const fresh = all.filter((e) => afterCursor(e, cursorAt, cursorId)).sort(byCursor);

    let lastAt = cursorAt;
    let lastId = cursorId;

    if (fresh.length > 0) {
      const lines: string[] = [];
      for (const entry of fresh) {
        lines.push(JSON.stringify({ seq: nextSeq, change: toWireChange(entry) }));
        nextSeq += 1;
      }
      // One append for the batch; a partial flush is the truncated tail that
      // `parseJournal` drops, never a hole in the middle.
      await target.appendFile(journalFileName(manifest.checkpointSeq), `${lines.join('\n')}\n`);
      pending += fresh.length;
      const last = fresh[fresh.length - 1];
      if (last) {
        lastAt = last.createdAt;
        lastId = last.changeId;
      }
    }

    let updated: LocalBackupManifest = {
      ...manifest,
      nextSeq,
      pendingSinceCheckpoint: pending,
      lastBackupAt: now,
      lastJournaledAt: lastAt,
      lastJournaledChangeId: lastId,
    };

    // Periodic full checkpoint so recovery never replays the whole history.
    if (pending >= CHECKPOINT_EVERY) {
      const seq = nextSeq - 1;
      const summary = await createBackup(db, { now });
      await target.writeFileAtomic(checkpointFileName(seq), summary.json);
      updated = {
        ...updated,
        checkpointSeq: seq,
        checkpointFile: checkpointFileName(seq),
        pendingSinceCheckpoint: 0,
      };
    }

    await writeManifest(target, updated);
    return { ok: true, status: statusFromManifest(target, updated) };
  } catch (e) {
    const error = errorMessage(e);
    return {
      ok: false,
      status: statusFromManifest(target, manifest, error),
      error,
    };
  }
}

/**
 * Re-open the folder the device already saved, then run an incremental backup.
 *
 * WHY THIS IS NOT `enableDiskBackup`
 * ----------------------------------
 * `enableDiskBackup` calls `pickFolder()`, which opens the directory picker. A
 * folder that is already saved and merely needs its write permission re-granted
 * must NOT go through that: the user would have to navigate to the same folder
 * again, and could pick a different one and orphan the existing backups. A browser
 * allows `requestPermission` on the saved handle inside a click handler, so this
 * path needs a user gesture but no picker.
 *
 * It therefore reuses the saved handle, never selects another folder, and only
 * appends/rewrites the files the normal incremental run writes. When no folder is
 * saved, `ensurePermission` reports false and the caller must offer "Turn on".
 */
export async function reconnectDiskBackup(
  target: DiskBackupTarget,
  db: DNRDatabase,
  now: string,
  userId?: string,
): Promise<DiskBackupRunResult> {
  if (!target.supported) {
    const error = 'Automatic disk backup is not supported on this device.';
    return { ok: false, status: statusFromManifest(target, null, error), error };
  }
  let permitted: boolean;
  try {
    permitted = await target.ensurePermission();
  } catch (e) {
    // A denied re-request is a real failure the panel must report, not a
    // reason to fall through to "turn it on from scratch".
    const error = errorMessage(e);
    return { ok: false, status: statusFromManifest(target, null, error), error };
  }
  if (!permitted) {
    const error = 'No backup folder is saved on this device. Choose a folder to turn automatic backup on.';
    return { ok: false, status: statusFromManifest(target, null, error), error };
  }
  return runDiskBackup(target, db, now, userId);
}

/** Current status without writing anything. */
export async function getDiskBackupStatus(target: DiskBackupTarget): Promise<DiskBackupStatus> {
  try {
    return statusFromManifest(target, await readManifestOrNull(target));
  } catch (error) {
    // A status poll must never reject: an unreadable folder or a revoked
    // permission is reported as an error the panel can display, not as an
    // unhandled promise in the console.
    return statusFromManifest(target, null, errorMessage(error));
  }
}

/**
 * Read and validate a backup folder WITHOUT touching the database, so the UI can
 * preview format, integrity, businesses and compatibility before any data is
 * replaced. Returns every failure as data, never by throwing.
 */
export async function readDiskBackup(target: DiskBackupTarget): Promise<DiskBackupReadResult> {
  const manifestText = await target.readFile(LOCAL_BACKUP_MANIFEST);
  if (manifestText === null) {
    return {
      ok: false,
      changes: [],
      problem: 'NOT_A_BACKUP',
      message: 'This folder does not contain a DNR Vyapar backup.',
    };
  }
  let parsedManifest: unknown;
  try {
    parsedManifest = JSON.parse(manifestText);
  } catch {
    return {
      ok: false,
      changes: [],
      problem: 'CORRUPT_MANIFEST',
      message: 'The backup manifest is unreadable.',
    };
  }
  const validation = validateManifest(parsedManifest);
  if (!validation.ok || !validation.manifest) {
    return {
      ok: false,
      changes: [],
      problem: validation.problem,
      message: validation.message ?? 'The backup manifest is invalid.',
    };
  }
  const manifest = validation.manifest;

  const checkpointText = await target.readFile(manifest.checkpointFile);
  if (checkpointText === null) {
    return {
      ok: false,
      manifest,
      changes: [],
      problem: 'MISSING_CHECKPOINT',
      message: `The checkpoint ${manifest.checkpointFile} is missing.`,
    };
  }
  let checkpoint: unknown;
  try {
    checkpoint = JSON.parse(checkpointText);
  } catch {
    return {
      ok: false,
      manifest,
      changes: [],
      problem: 'MISSING_CHECKPOINT',
      message: 'The checkpoint file is unreadable.',
    };
  }

  const journalText = await target.readFile(journalFileName(manifest.checkpointSeq));
  const parsed = parseJournal(journalText ?? '');
  if (parsed.problem) {
    return {
      ok: false,
      manifest,
      changes: [],
      problem: parsed.problem,
      message: parsed.message ?? 'The backup journal is unreadable.',
    };
  }
  if (!journalSequencesOk(parsed.lines, manifest.checkpointSeq + 1)) {
    return {
      ok: false,
      manifest,
      changes: [],
      problem: 'SEQ_GAP',
      message: 'The backup journal is missing entries and cannot be replayed safely.',
    };
  }

  const changes: SyncChange[] = [];
  for (const line of parsed.lines) {
    if (typeof line.change !== 'object' || line.change === null) {
      return {
        ok: false,
        manifest,
        changes: [],
        problem: 'CORRUPT_JOURNAL',
        message: `Journal entry ${line.seq} carries no change.`,
      };
    }
    changes.push(line.change as SyncChange);
  }

  return { ok: true, manifest, checkpoint, changes };
}

export interface RestoreFromDiskOptions {
  /** Must be true — a restore replaces working data. */
  readonly confirmed: boolean;
  readonly now: string;
  /** Restrict the checkpoint restore. Defaults to every business in the file. */
  readonly businessIds?: readonly string[];
}

/**
 * Restore from a disk folder: latest checkpoint, then replay the journal tail.
 *
 * On ANY failure the error propagates and the working database is left as it was
 * — the checkpoint restore is a single transaction (see `restoreBackup`) and the
 * journal replay only runs after it has committed, so there is no partial state.
 */
export async function restoreFromDiskBackup(
  target: DiskBackupTarget,
  db: DNRDatabase,
  options: RestoreFromDiskOptions,
): Promise<DiskBackupRestoreResult> {
  const read = await readDiskBackup(target);
  if (!read.ok || read.checkpoint === undefined || !read.manifest) {
    throw new Error(read.message ?? 'That folder is not a usable DNR Vyapar backup.');
  }

  // 1. Replace the working database with the checkpoint, in one transaction.
  const summary = await restoreBackup(db, read.checkpoint, {
    confirmed: options.confirmed,
    businessIds: options.businessIds,
    now: options.now,
  });

  // 2. Replay the journal tail on top, grouped by business. `applyChanges` is
  //    version-aware, so replay that overlaps the checkpoint is idempotent and
  //    never duplicates an invoice, payment or movement.
  let applied = 0;
  let skipped = 0;
  const scope = options.businessIds;
  const replay = scope ? read.changes.filter((c) => scope.includes(c.businessId)) : read.changes;
  if (replay.length > 0) {
    const deviceId = await currentDeviceId(db);
    const groups = new Map<string, SyncChange[]>();
    for (const change of replay) {
      const existing = groups.get(change.businessId);
      if (existing) existing.push(change);
      else groups.set(change.businessId, [change]);
    }
    for (const [businessId, changes] of groups) {
      const result = await applyChanges({
        db,
        businessId,
        deviceId,
        changes,
        now: options.now,
        fromSnapshot: false,
      });
      applied += result.applied;
      skipped += result.skipped;
    }
  }

  // 3. Re-open a business session. The checkpoint's `settings` normally restores
  //    the active id; this only fills a gap so the app does not land on a gate.
  let active = await readActiveBusinessId(db);
  if (active === null) {
    const first = read.manifest.businessIds[0];
    if (first) {
      await setActiveBusinessId(db, first);
      active = first;
    }
  }

  return {
    restored: summary.restored,
    journalApplied: applied,
    journalSkipped: skipped,
    activeBusinessId: active,
  };
}
